import { ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import {
  addCategory,
  deleteCategory,
  renameCategory,
  reorderCategories,
  restoreCategory,
  setCategoryHidden,
} from '../../../data/commands/spending';
import { useCommandEnv } from '../../../data/hooks';
import type { SpendingCategory } from '../../../data/repos/spending';
import { Button, ScreenHeader, Sheet, showToast } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { copy, errorText, SPENDING_BACK } from '../copy';
import { categoryColor } from '../format';
import { useCategories } from '../hooks';
import { CARD, FOCUS, ICON_BTN, INPUT, LABEL, SCREEN } from '../styles';

function Dot({ color }: { color: string }) {
  return (
    <svg width="10" height="10" aria-hidden="true" className="shrink-0">
      <circle cx="5" cy="5" r="5" fill={color} />
    </svg>
  );
}

/** Rename, hide or delete one category. Delete is a soft delete with Undo in the toast. */
function EditCategorySheet({
  category,
  onClose,
}: {
  category: SpendingCategory | null;
  onClose(): void;
}) {
  const env = useCommandEnv();
  const formId = useId();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(category?.name ?? '');
    setError(null);
  }, [category]);

  if (!category) return null;
  const c = category;

  async function save() {
    if (name.trim() === c.name) return onClose();
    const r = await renameCategory(env, c.id, name);
    if (!r.ok) return setError(errorText(r.error));
    onClose();
  }

  async function toggleHidden() {
    await setCategoryHidden(env, c.id, !c.hidden);
    onClose();
  }

  async function remove() {
    await deleteCategory(env, c.id);
    onClose();
    showToast({
      message: copy.categoryDeleted(c.name),
      action: { label: copy.undo, onPress: () => void restoreCategory(env, c.id) },
    });
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && onClose()}
      title={copy.editCategoryTitle}
      footer={
        <Button type="submit" form={formId} variant="primary" className="min-h-12 w-full">
          {copy.save}
        </Button>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-5 pb-2"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <TextField
          label={copy.categoryName}
          value={name}
          maxLength={40}
          enterKeyHint="done"
          data-autofocus=""
          error={error}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex flex-col gap-2 border-t border-divider pt-4">
          <Button onClick={() => void toggleHidden()} className="justify-start">
            {c.hidden ? copy.showInQuickAdd : copy.hideFromQuickAdd}
          </Button>
          {c.hidden ? null : <p className="px-1 text-xs text-muted">{copy.hideHint}</p>}
          <Button
            variant="quiet-destructive"
            onClick={() => void remove()}
            className="mt-2 justify-start"
          >
            {copy.deleteCategory}
          </Button>
          <p className="px-1 text-xs text-muted">{copy.deleteHint}</p>
        </div>
      </form>
    </Sheet>
  );
}

export function CategoriesScreen() {
  const env = useCommandEnv();
  const categories = useCategories() ?? [];
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const visible = categories.filter((c) => !c.hidden);
  const hidden = categories.filter((c) => c.hidden);
  const order = categories.map((c) => c.id);
  const editing = categories.find((c) => c.id === editingId) ?? null;

  async function move(index: number, delta: -1 | 1) {
    const ids = visible.map((c) => c.id);
    const j = index + delta;
    const a = ids[index];
    const b = ids[j];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[j] = a;
    await reorderCategories(env, [...ids, ...hidden.map((c) => c.id)]);
  }

  async function add() {
    const r = await addCategory(env, newName);
    if (!r.ok) return setError(errorText(r.error));
    setNewName('');
    setError(null);
  }

  return (
    <main className={`${SCREEN} md:max-w-2xl`}>
      <header className="flex flex-col gap-1">
        <ScreenHeader title={copy.categoriesTitle} back={SPENDING_BACK} />
        <p className="text-sm text-muted">{copy.categoriesIntro}</p>
      </header>

      {visible.length === 0 ? <p className="text-muted">{copy.noCategories}</p> : null}
      <ul aria-label={copy.categoriesTitle} className={`${CARD} divide-y divide-divider py-1`}>
        {visible.map((c, i) => (
          <li key={c.id} className="flex items-center gap-1.5 py-1">
            <button
              type="button"
              aria-label={copy.editCategoryLabel(c.name)}
              onClick={() => setEditingId(c.id)}
              className={`flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-xl px-1 text-left leading-tight text-fg hover:bg-surface ${FOCUS}`}
            >
              <Dot color={categoryColor(c.id, order)} />
              <span className="min-w-0 flex-1 break-words">{c.name}</span>
              <Pencil aria-hidden className="h-4 w-4 shrink-0 text-muted" />
            </button>
            <button
              type="button"
              aria-label={copy.moveUp(c.name)}
              disabled={i === 0}
              onClick={() => void move(i, -1)}
              className={ICON_BTN}
            >
              <ChevronUp aria-hidden className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label={copy.moveDown(c.name)}
              disabled={i === visible.length - 1}
              onClick={() => void move(i, 1)}
              className={ICON_BTN}
            >
              <ChevronDown aria-hidden className="h-5 w-5" />
            </button>
          </li>
        ))}
      </ul>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <input
          aria-label={copy.newCategory}
          placeholder={copy.newCategory}
          value={newName}
          maxLength={40}
          enterKeyHint="done"
          onChange={(e) => setNewName(e.target.value)}
          className={`${INPUT} min-w-0 flex-1`}
        />
        <Button type="submit" variant="secondary">
          {copy.add}
        </Button>
      </form>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {hidden.length > 0 ? (
        <section aria-label={copy.hiddenTitle} className={CARD}>
          <h2 className={`mb-2 ${LABEL}`}>{copy.hiddenTitle}</h2>
          <ul className="divide-y divide-divider">
            {hidden.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 py-1">
                <span className="min-w-0 break-words text-muted">{c.name}</span>
                <div className="flex shrink-0">
                  <Button
                    variant="ghost"
                    aria-label={copy.editCategoryLabel(c.name)}
                    onClick={() => setEditingId(c.id)}
                  >
                    {copy.edit}
                  </Button>
                  <Button variant="ghost" onClick={() => void setCategoryHidden(env, c.id, false)}>
                    {copy.unhide}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <EditCategorySheet category={editing} onClose={() => setEditingId(null)} />
    </main>
  );
}
